import { Log } from '@shared/debug/log';

const ALLOWED_TAGS = new Set(['p', 'span', 'u', 'font', 'b', 'i', 'br', 'textformat']);

const ALLOWED_ATTRIBUTES: Readonly<Record<string, ReadonlySet<string>>> = {
  // Aligned with XmlLabelTextValidator
  p: new Set(['align']),
  span: new Set(),
  strong: new Set(),
  em: new Set(),
  u: new Set(),
  font: new Set(['face', 'size', 'color', 'letterspacing', 'kerning']),
  b: new Set(),
  i: new Set(),
  br: new Set(),
  textformat: new Set(['leading', 'indent', 'blockindent', 'leftmargin', 'rightmargin', 'tabstops']),
};

/**
 * Sanitizes label html5data strings loaded from the backend.
 * Only allowed formatting tags and their whitelisted attributes are kept.
 * Disallowed elements are unwrapped (children are kept, the element itself is removed).
 * Source tag and attribute casing is preserved; the output matches the input spelling for all allowed tokens.
 * This function must be called exactly once per value, directly after loading from the backend.
 */
export class LabelHtmlSanitizer {

  public static sanitize(html: string): string {
    if (!html) {
      return '';
    }
    // Normalize line breaks to self-closing XML form and replace &nbsp; which is not a valid XML entity.
    const preprocessed = html
      .replaceAll(/<br\s*\/?>/gi, '<br/>')
      .replaceAll('&nbsp;', ' ');

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<root>${preprocessed}</root>`, 'application/xml');

    if (doc.querySelector('parsererror')) {
      Log.error('LabelHtmlSanitizer: malformed XML input - returning empty string');
      return '';
    }

    const root = doc.documentElement;
    LabelHtmlSanitizer.sanitizeChildren(root);

    const serialized = new XMLSerializer().serializeToString(root);
    return serialized
      .replace(/^<root[^>]*>/, '')
      .replace(/<\/root>$/, '')
      .replaceAll(' xmlns=""', '');
  }

  private static sanitizeChildren(parent: Element): void {
    // Snapshot child nodes before mutating the DOM
    const children = Array.from(parent.childNodes);
    for (const node of children) {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        continue; // Text nodes are safe and must not be touched
      }
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      if (ALLOWED_TAGS.has(tagName)) {
        LabelHtmlSanitizer.sanitizeAttributes(element, tagName);
        LabelHtmlSanitizer.sanitizeChildren(element);
      } else {
        // Recurse first so the children are clean before being moved up
        LabelHtmlSanitizer.sanitizeChildren(element);

        // Unwrap: move sanitized children to parent, then remove the disallowed element
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        element.remove();
      }
    }
  }

  private static sanitizeAttributes(element: Element, tagName: string): void {
    const allowed = ALLOWED_ATTRIBUTES[tagName] ?? (new Set<string>());
    const toRemove: string[] = [];
    for (const attr of Array.from(element.attributes)) {
      if (!allowed.has(attr.name.toLowerCase())) {
        toRemove.push(attr.name);
      }
    }
    for (const attr of toRemove) {
      element.removeAttribute(attr);
    }
  }
}
